import { Component, OnInit, OnDestroy, ViewChild, TemplateRef, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User } from '../../models/user';
import { LOSJsonPath } from '../../models/los-field-path';
import { FieldPathService } from '../../services/field-path.service';
import { ToastService } from 'src/app/services/toast.service';
import { UntypedFormControl, Validators, UntypedFormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { createAddButton, createDeleteButton, createEditButton } from 'src/app/features/grid/classes/grid-config.types';

@Component({
  selector: 'app-field-paths-view',
  templateUrl: './field-paths-view.component.html',
  styleUrls: ['./field-paths-view.component.scss'],
  providers: [FieldPathService],
  standalone: false,
})
export class FieldPathsViewComponent implements OnInit, OnDestroy {

  @ViewChild('addUpdateFieldForm', { static: true }) addUpdateFieldForm: TemplateRef<any>;
  componentDestroyed$ = new Subject<boolean>();

  encompassFieldPaths: LOSJsonPath[];
  selectedItem: LOSJsonPath;

  private savingFieldPath = false;

  public isNew = false;

  fieldIdFormControl = new UntypedFormControl('', { validators: [Validators.required], updateOn: 'blur' });
  apiFieldPathFormControl = new UntypedFormControl('', { validators: [Validators.required], updateOn: 'blur' });

  // Grid data
  gridSettingsReady = false;
  public fieldPathGridSettings = signal<GridSettings>(null);

  // Component variables
  public gridLoading = signal<boolean>(true);
  
  constructor(
    protected fieldPathService: FieldPathService,
    protected toastService: ToastService,
    private dialog: MatDialog,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private user: User,
  ) {
  }

  ngOnInit(): void {
    this.fieldPathGridSettings.set(this.setupGrid());
    this.gridSettingsReady = true;

    this.fieldPathService.getAllFieldPaths()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (r) => {
          this.encompassFieldPaths = r;
          this.fieldPathGridSettings.set(this.fieldPathGridSettings().setData(this.encompassFieldPaths));
          this.gridLoading.set(false);
        },
        error: (_error) => {
          // console.log(error);
        },
      });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
  
  setupGrid(): GridSettings {
    return new GridSettings({
      loading: this.gridLoading,
      showAddButton: true,
      addButtonDetails: createAddButton({
        label: 'Add New',
        tooltip: 'Add new field path mapping',
      }),
      dataKey: 'losJsonPathId',
      data: this.encompassFieldPaths,
      fixedGrid: true,
      columns: [
        new GridColumnData({ width: '20%', field: 'fieldId', header: 'Field Id', exportable: true}),
        new GridColumnData({ width: '70%', bodyStyleClass: 'text-wrap', field: 'apiFieldPath', header: 'API Field Path', exportable: true}),
        new GridColumnData({ width: '15%', filterable: false, field: 'updatedDate', header: 'Updated Date', type: 'date', format: 'MM/dd/yyyy h:mm a', exportable: true}),
      ],
      sortMode: 'single',
      filter: {
        enabled: true,
        display: 'menu',
        mode: 'row',
      },
      commandsColumn: {
        enabled: true,
        headerText: 'Actions',
        layout: 'horizontal',
        buttonSpacing: '0.25rem',
        buttons: [
          createEditButton({
            label: 'Edit',
            showIcon: false,
            size: 'small',
          }),
          createDeleteButton({
            label: '',
            showIcon: true,
            size: 'small',
          }),
        ],
      },
      export: {
        enabled: true,
        formats: ['csv'],
        fileName: 'LOSJsonPaths',
      },
    });
  }


  checkAccess(role: string[]): boolean {
    return this.user.checkAccess(role);
  }

  public onSave(): void {
    this.savingFieldPath = true;
    let validFields = true;

    if (this.isNew && this.fieldIdFormControl.status !== 'VALID') {
      this.toastService.toastCreate('Field Id is invalid. Try again.', 'Danger');
      validFields = false;
    }
    else if (this.apiFieldPathFormControl.status !== 'VALID') {
      this.toastService.toastCreate('Field Id and/or Field Path is invalid. Try again.', 'Danger');
      validFields = false;
    }
    else if (!this.isNew && (this.apiFieldPathFormControl.value === this.selectedItem.apiFieldPath)) {
      this.toastService.toastCreate('No change in api field path to save. Skipping save.', 'Warning.');
      validFields = false;
    }
    else if (this.isNew && this.encompassFieldPaths.filter(fieldPath => fieldPath.fieldId == this.fieldIdFormControl.value).length > 0) {
      this.toastService.toastCreate('Field Id already exists.', 'Danger');
      validFields = false;
    }

    if (validFields == false) {
      this.savingFieldPath = false;
      return;
    }

    this.fieldIdFormControl.disable();
    this.apiFieldPathFormControl.disable();

    this.selectedItem.apiFieldPath = this.apiFieldPathFormControl.value;
    this.selectedItem.updatedDate = new Date();

    if (this.isNew == true) {
      this.selectedItem.fieldId = this.fieldIdFormControl.value;

      this.fieldPathService.insertLOSJsonPath(this.selectedItem)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (_value) => {
            this.toastService.toastCreate(
              'Successfully added field: ' + this.selectedItem.fieldId,
              'Success', { keepAfterRouteChange: true },
            );
            this.encompassFieldPaths.push(this.selectedItem);
            this.dialog.closeAll();
            this.fieldPathGridSettings.set(this.fieldPathGridSettings().setData(this.encompassFieldPaths));
          },
          error: (_error) => {
            this.toastService.toastCreate('Failed to add field: ' + this.selectedItem.fieldId, 'Warning');
          },
          complete: () => {
            this.savingFieldPath = false;
            this.fieldIdFormControl.enable();
            this.apiFieldPathFormControl.enable();
          },
        });
    }
    else {
      this.fieldPathService.updateLOSJsonPath(this.selectedItem)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (_value) => {
            this.toastService.toastCreate('Successfully updated field: ' + this.selectedItem.fieldId,
              'Success', { keepAfterRouteChange: true },
            );
            this.dialog.closeAll();
          },
          error: (_error) => {
            this.toastService.toastCreate('Failed to update field: ' + this.selectedItem.fieldId, 'Warning');
          },
          complete: () => {
            this.savingFieldPath = false;
            this.fieldIdFormControl.enable();
            this.apiFieldPathFormControl.enable();
          },
        });
    }
  }

  public onCancel(): void {
    this.dialog.closeAll();
    this.selectedItem = null;
  }

  public setUpdating(event): void {
    this.selectedItem = event;
    this.isNew = false;
    this.fieldIdFormControl.disable();
    this.fieldIdFormControl.setValue(this.selectedItem.fieldId);
    this.apiFieldPathFormControl.setValue(this.selectedItem.apiFieldPath);
    this.dialog.open(this.addUpdateFieldForm);
  }

  public setAdding(): void {
    this.selectedItem = new LOSJsonPath();
    this.isNew = true;
    this.fieldIdFormControl.enable();
    this.fieldIdFormControl.setValue('');
    this.apiFieldPathFormControl.setValue('');
    this.dialog.open(this.addUpdateFieldForm);
  }

  public deleteFieldPath(event): void {
    const confirmMsg =
      'Are you sure you want to delete the field path for id ' + event.fieldId + '?';
    if (window.confirm(confirmMsg)) {
      this.fieldPathService.deleteLOSJsonPath(event)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: (_value) => {
              this.toastService.toastCreate('Successfully deleted field path: ' + event.fieldId,
                'Success', { keepAfterRouteChange: true },
              );
              
              this.encompassFieldPaths = this.encompassFieldPaths.filter(fp => fp.losJsonPathId != event.losJsonPathId);
              this.fieldPathGridSettings.set(this.fieldPathGridSettings().setData(this.encompassFieldPaths));
            },
            error: (_error) => {
              this.toastService.toastCreate('Failed to delete field path: ' + event.fieldId, 'Warning');
            },
          });
    }
  }
}
